import React, { useReducer, useRef, useLayoutEffect, useEffect, useState } from 'react';
import axios from 'axios';
import I18n from 'i18n-js';
import cn from '@/utils/cn';
import * as Routes from 'modules/routes';
import { DndProvider } from 'react-dnd';
import { MultiBackend } from 'react-dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import DndAnswerContext from 'components/common/Context';
import clsx from 'clsx';
import ResetReadingGameBtn from '@/components/common/ResetReadingGameBtn';
import useProgramContext from '@/hooks/useProgramContext';
import ClearButton from '@/components/common/Forms/Questions/DragAndDrop/Answer/DraggableChoicesBank/ClearButton';
import useFlags from '@/hooks/useFlags';
import { IQuestion } from '@/components/staffer/Grading/types';
import styles from './Answer.module.scss';
import bankStyles from './DraggableChoicesBank/DraggableChoicesBank.module.scss';
import { DragAndDropQuestionType, DraggableContainerPosition } from '../types';
import answerChoiceReducer from './reducers/answerChoiceReducer';
import DraggableChoicesBank from './DraggableChoicesBank';
import DropZoneArea from './DropZoneArea';
import useQuestionAnswerSave from './DraggableChoicesBank/useQuestionAnswerSave';
import QuestionAnswer from '../../../../../interfaces/QuestionAnswer';
import { usePreviousAnswers, useSubmitDisabled } from './hooks/helpers';
import SubmissionTextHelper from '../../../SubmissionTextHelper';
import { formatVocab } from '../utils';
import useVocabularyStore from '../useVocabularyStore';
import CustomDragLayer from './CustomDragLayer';

interface DragAndDropProps {
  inBuilder?: boolean;
  gradingView?: boolean;
  inheritedScaleValue?: number;
  isSubmittable?: boolean;
  lessonId?: number;
  locale: Locale;
  parentContainerId: string;
  progressIcon?: React.ReactNode;
  question: DragAndDropQuestionType | IQuestion;
  readingGameId?: number,
  shouldDisplayPrompt?: boolean,
  showAnswerKey?: boolean;
  slideShowId?: number,
  studentId?: string;
  useClearButton?: boolean;
  useFloatingSubmitBtn?: boolean;
  useReadingGameResetBtn?: boolean;
}

export const DIMENSIONS = {
  height: 540,
  width: 960,
};

const DragAndDrop = ({
  inBuilder = false,
  gradingView = false,
  inheritedScaleValue = 1,
  isSubmittable = false,
  locale,
  parentContainerId,
  progressIcon,
  question,
  readingGameId,
  showAnswerKey = false,
  shouldDisplayPrompt = true,
  slideShowId = null,
  studentId = null,
  useClearButton = false,
  useFloatingSubmitBtn = false,
  useReadingGameResetBtn,
}: DragAndDropProps) => {
  const { lessonId } = useProgramContext();
  const dragAndDropContainerRef = useRef();
  const [questionAnswer, setQuestionAnswer] = useState<QuestionAnswer>();
  const [loading, setLoading] = useState(true);
  const setVocabularyTerms = useVocabularyStore(state => state.setVocabularyTerms);
  const scalar = useRef(0);
  const setVocabLoading = useVocabularyStore(state => state.setLoading);
  const { scaleVocabDnd } = useFlags();

  useEffect(() => {
    if (!lessonId) return;

    const route = Routes.plato_api_get_glossary_terms_glossary_terms_path({ lesson_id: lessonId, locale });

    axios.get(route)
      .then((response) => {
        if (response.data.glossary_terms) setVocabularyTerms(formatVocab(response.data.glossary_terms));
        setVocabLoading(false);
      });
  }, []);

  const {
    background_image: background,
    body: questionBody,
    id: questionId,
    data: questionData,
  } = question;

  const {
    correct_answers: correctAnswers,
    draggable_choices: draggableChoices,
    draggable_container_position: draggableContainerPosition,
    drop_zones: dropZones,
    text_areas: textAreas,
    has_reusable_answer_choices: hasReusableAnswerChoices,
    has_transparent_answer_choices: hasTransparentAnswerChoices,
    has_transparent_dropzones: hasTransparentDropzones,
    vocab_link_option: vocabLinkOption,
    is_vocab_dnd: isVocabDnD,
  } = questionData;

  const doResize = () => {
    const el = $(dragAndDropContainerRef.current);
    const parentContainer = $(`#${parentContainerId}`);
    const elHeight = el.outerHeight();
    const elWidth = el.outerWidth();
    const parentHeight = parentContainer.height();
    const parentWidth = parentContainer.width();
    const scale = Math.min(
      parentWidth / elWidth,
      parentHeight / elHeight,
    );
    const marginLeft = (parentWidth - (elWidth * scale)) / 2;
    scalar.current = scale;

    // The base layout uses absolute positioning against DIMENSIONS (960x540). For vocab DnDs
    // we fill the parent 1:1 instead of scaling — but only when the parent is at least the base
    // size, otherwise the inner absolute-positioned content overflows (e.g. grading thumbnails).
    const shouldFillContainer = scaleVocabDnd && isVocabDnD
      && parentWidth >= DIMENSIONS.width
      && parentHeight >= DIMENSIONS.height;

    el.css(shouldFillContainer ? {
      height: `${parentHeight}px`,
      width: `${parentWidth}px`,
    } : {
      WebkitTransform: `scale(${scale})`,
      height: `${DIMENSIONS.height}px`,
      marginLeft: `${marginLeft}px`,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      width: `${DIMENSIONS.width}px`,
    });
  };

  useLayoutEffect(() => {
    // resize twice to get the most accurate sizing after react refreshes the component
    doResize();
    doResize();

    // Set the parent container height after resizing to trim the overflow created by scaling
    const $parentContainer = $(`#${parentContainerId}`);
    const parentWidth = $parentContainer.width() ?? 0;
    const parentHeight = $parentContainer.outerHeight() ?? 0;
    const filledByVocabDnd = scaleVocabDnd && isVocabDnD
      && parentWidth >= DIMENSIONS.width
      && parentHeight >= DIMENSIONS.height;
    if ((parentHeight !== 0 || $parentContainer.outerWidth() !== 0) && !filledByVocabDnd) {
      $parentContainer.css({ height: `${DIMENSIONS.height * scalar.current}px` });
    }
  }, [parentContainerId, showAnswerKey]);

  const [answerChoices, answerChoiceDispatch] = useReducer(answerChoiceReducer, {});

  useEffect(() => {
    setQuestionAnswer(null);
  }, [question.id]);

  useEffect(() => {
    setLoading(true);

    axios
      .get(Routes.plato_api_question_answers_path({
        question_id: question.id,
        slideshow_id: slideShowId,
        student_id: studentId,
      }))
      .then((res) => {
        if (res.data.length === 0) return;
        if (res.data.question_answers.length === 0) return;

        setQuestionAnswer(res.data.question_answers[0]);
        const storedAnswer = JSON.parse(res.data.question_answers[0].answer);

        answerChoiceDispatch({
          payload: storedAnswer,
          type: 'INITIALIZE_FROM_DB',
        });
      }).catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [question.id]);

  useLayoutEffect(() => {
    window.addEventListener('toggled', doResize);
  }, []);

  useEffect(() => {
    if (loading) return;
    window.dispatchEvent(new Event('questionLoaded'));
  }, [loading]);

  const questionAnswerSave = useQuestionAnswerSave({
    correctChoices: correctAnswers,
    isSubmittable,
    loading,
    parseAnswer: (qa: QuestionAnswer) => JSON.parse(qa.answer),
    pointsEarned: questionAnswer?.earned_points || 0,
    pointsPerDraggable: questionAnswer?.answer_data?.points_per_draggable || {},
    previousAnswers: questionAnswer?.answer_data?.attempts_made,
    questionId,
    slideShowId,
    values: answerChoices,
  });

  const submissionTextHelper = new SubmissionTextHelper({
    correctChoices: questionAnswerSave.correctChoices,
    hasSubmitted: questionAnswerSave.previousAnswers.length > 0,
    maxAttempts: 2,
    maxPoints: question.points,
    pointsEarned: questionAnswerSave.pointsEarned,
    previousAnswers: questionAnswerSave.previousAnswers,
    questionType: 'DragAndDrop',
    submitting: questionAnswerSave.submitting,
    value: questionAnswerSave.value,
  });

  const isSubmitDisabled = useSubmitDisabled({
    answerChoices,
    isComplete: submissionTextHelper.hasCorrectAnswer(),
    isSubmitting: questionAnswerSave.submitting,
    previousAnswers: questionAnswerSave.previousAnswers,
    value: questionAnswerSave.value,
  });

  const previousAnswers = usePreviousAnswers({ previousAnswers: questionAnswerSave.previousAnswers });
  const isComplete = submissionTextHelper.hasCorrectAnswer() || questionAnswerSave.previousAnswers.length === 2 || gradingView;
  const cumulativeDndScalar = inheritedScaleValue * (scalar ? scalar.current : 1);
  const isHorizontallyPositioned = [
    DraggableContainerPosition.Top, DraggableContainerPosition.Bottom,
  ].includes(draggableContainerPosition);

  return (
    <DndAnswerContext.Provider
      value={{
        answerChoiceDispatch,
        answerChoices,
        background,
        correctAnswers,
        draggableChoices,
        dropZones,
        hasReusableAnswerChoices,
        hasTransparentAnswerChoices,
        hasTransparentDropzones,
        isComplete,
        locale,
        parentContainerId,
        pointsPerDraggable: questionAnswerSave.pointsPerDraggable,
        previousAnswers: questionAnswerSave.previousAnswers,
        question,
        questionBody,
        questionId,
        showAnswerKey,
        studentId,
        textAreas,
        useFloatingSubmitBtn,
        vocabLinkOption,
      }}
    >
      <section
        ref={dragAndDropContainerRef}
        className={cn(
          styles.dragAndDropQuestionContainer, {
            'tw-flex-col': draggableContainerPosition === DraggableContainerPosition.Top,
            'tw-flex-col-reverse': draggableContainerPosition === DraggableContainerPosition.Bottom,
            'tw-flex-row-reverse': draggableContainerPosition === DraggableContainerPosition.Right,
          },
        )}
        id={`question_${questionId}_body`}
      >
        <DndProvider backend={MultiBackend} options={HTML5toTouch}>
          {!loading && (
            <>
              {progressIcon}
              <DraggableChoicesBank
                correctChoices={questionAnswerSave.correctChoices}
                currentChoices={answerChoices}
                isComplete={isComplete}
                isHorizontallyPositioned={isHorizontallyPositioned}
                isSubmitDisabled={isSubmitDisabled}
                isSubmittable={isSubmittable}
                savedChoices={previousAnswers}
                shouldDisplayPrompt={shouldDisplayPrompt}
                submissionTextHelper={submissionTextHelper}
                useClearButton={useClearButton}
                onSubmit={questionAnswerSave.submit}
              />
              <DropZoneArea
                correctChoices={questionAnswerSave.correctChoices}
                cumulativeDndScalar={cumulativeDndScalar}
                draggableContainerPosition={draggableContainerPosition}
                inBuilder={inBuilder}
                isComplete={isComplete}
                isDragDisabled={questionAnswerSave.submitting || isComplete}
              />
              <CustomDragLayer />
            </>
          )}
        </DndProvider>
      </section>
      {useFloatingSubmitBtn && isSubmittable && (
        <div className={clsx(bankStyles.submissionContainerWrapper, { [bankStyles.floating]: useFloatingSubmitBtn })}>
          {submissionTextHelper.submissionAlert()}
          <div className={styles.buttonRow}>
            <button
              className="btn btn--purple"
              disabled={isSubmitDisabled}
              tabIndex={0}
              type="button"
              onClick={() => questionAnswerSave.submit(true)}
            >
              {I18n.t('submit')}
            </button>
            {useClearButton && (
              <ClearButton
                correctChoices={questionAnswerSave.correctChoices}
                currentChoices={answerChoices}
                isComplete={isComplete}
                savedChoices={previousAnswers}
              />
            )}
            {useReadingGameResetBtn && readingGameId && (
              <ResetReadingGameBtn
                disabled={Object.keys(answerChoices).length === 0 && questionAnswerSave.previousAnswers.length === 0}
                readingGameId={readingGameId}
              />
            )}
          </div>
        </div>
      )}
    </DndAnswerContext.Provider>
  );
};

export default DragAndDrop;
